import { Search, X } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import DateRangeFilter from './DateRangeFilter';
import DurationRangeFilter from './DurationRangeFilter';


interface CallFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  dateFilter: string;
  onDateFilterChange: (filter: string) => void;
  durationFilter: string;
  onDurationFilterChange: (filter: string) => void;
  customDateRange?: {
    startDate: string;
    endDate: string;
  };
  onCustomDateRangeChange?: (startDate: string, endDate: string) => void;
  customDurationRange?: {
    minDuration: number;
    maxDuration: number;
  };
  onCustomDurationRangeChange?: (minDuration: number, maxDuration: number) => void;
  filteredCount: number;
  totalCount: number;
}

export default function CallFilters({
  searchQuery,
  onSearchChange,
  dateFilter,
  onDateFilterChange,
  durationFilter,
  onDurationFilterChange,
  customDateRange,
  onCustomDateRangeChange,
  customDurationRange,
  onCustomDurationRangeChange,
  filteredCount,
  totalCount
}: CallFiltersProps) {
  const isDarkMode = useTheme((state) => state.theme);

  const hasActiveFilters = searchQuery || dateFilter !== 'all' || durationFilter !== 'all';

  const clearAllFilters = () => {
    onSearchChange('');
    onDateFilterChange('all');
    onDurationFilterChange('all');
  };

  const getDateLabel = () => {
    if (dateFilter === 'custom' && customDateRange) {
      const startDate = new Date(customDateRange.startDate).toLocaleDateString();
      const endDate = new Date(customDateRange.endDate).toLocaleDateString();
      return `${startDate} - ${endDate}`;
    }
    
    const dateOptions: Record<string, string> = {
      'all': 'All Time',
      'today': 'Today',
      'week': 'This Week',
      'month': 'This Month',
      'quarter': 'Last 3 Months'
    };
    
    return dateOptions[dateFilter] || dateFilter;
  };

  const getDurationLabel = () => {
    if (durationFilter === 'custom' && customDurationRange) {
      const formatDuration = (minutes: number) => {
        if (minutes < 60) return `${minutes}min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
      };
      return `${formatDuration(customDurationRange.minDuration)} - ${formatDuration(customDurationRange.maxDuration)}`;
    }
    
    const durationOptions: Record<string, string> = {
      'all': 'All Durations',
      'short': '< 5min',
      'medium': '5-15min',
      'long': '> 15min'
    };
    
    return durationOptions[durationFilter] || durationFilter;
  };

  return (
    <div className={`rounded-xl p-6 shadow-lg ${
      isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white'
    }`}>
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`} />
          <input
            type="text"
            placeholder="Search customer name or representative..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`w-full pl-10 pr-10 py-3 rounded-lg focus:ring-2 focus:ring-[#1a9f6b] outline-none transition-all ${
              isDarkMode
                ? 'bg-[#050a0a] text-white placeholder-gray-400 border border-[#1a2424] focus:border-[#1a9f6b]'
                : 'bg-gray-100 text-[#053333] placeholder-gray-500 border border-gray-300 focus:border-[#1a9f6b] focus:bg-white'
            }`}
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-opacity-20 transition-colors ${
                isDarkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-500 hover:bg-gray-200'
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter Controls Row */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Date Range Filter */}
          <DateRangeFilter
            value={dateFilter}
            onChange={onDateFilterChange}
            customStartDate={customDateRange?.startDate}
            customEndDate={customDateRange?.endDate}
            onCustomDateChange={onCustomDateRangeChange}
          />

          {/* Duration Range Filter */}
          <DurationRangeFilter
            value={durationFilter}
            onChange={onDurationFilterChange}
            customMinDuration={customDurationRange?.minDuration}
            customMaxDuration={customDurationRange?.maxDuration}
            onCustomDurationChange={onCustomDurationRangeChange || (() => {})}
          />

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
              }`}
            >
              <X className="w-4 h-4" />
              Clear All
            </button>
          )}
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-opacity-20">
            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Active filters:
            </span>
            
            {searchQuery && (
              <span className={`px-3 py-1 rounded-full text-xs flex items-center gap-2 ${
                isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
              }`}>
                Search: "{searchQuery}"
                <button 
                  onClick={() => onSearchChange('')}
                  className="hover:bg-black hover:bg-opacity-20 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {dateFilter !== 'all' && (
              <span className={`px-3 py-1 rounded-full text-xs flex items-center gap-2 ${
                isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
              }`}>
                Date: {getDateLabel()}
                <button 
                  onClick={() => onDateFilterChange('all')}
                  className="hover:bg-black hover:bg-opacity-20 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {durationFilter !== 'all' && (
              <span className={`px-3 py-1 rounded-full text-xs flex items-center gap-2 ${
                isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'
              }`}>
                Duration: {getDurationLabel()}
                <button 
                  onClick={() => onDurationFilterChange('all')}
                  className="hover:bg-black hover:bg-opacity-20 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Results Counter */}
        {filteredCount !== totalCount && (
          <div className={`text-sm pt-2 border-t border-opacity-10 ${
            isDarkMode ? 'text-gray-400 border-gray-600' : 'text-gray-600 border-gray-300'
          }`}>
            Showing <span className="font-medium text-[#1a9f6b]">{filteredCount}</span> of{' '}
            <span className="font-medium">{totalCount}</span> recordings
          </div>
        )}
      </div>
    </div>
  );
}